import type { ReactNode, SVGProps } from 'react'

export type IconName =
  | 'menu'
  | 'search'
  | 'plus'
  | 'minus'
  | 'close'
  | 'check'
  | 'chevron-down'
  | 'chevron-right'
  | 'bell'
  | 'clock'
  | 'play'
  | 'pause'
  | 'settings'
  | 'more'
  | 'at'
  | 'my-tasks'
  | 'kanban'
  | 'gantt'
  | 'report'
  | 'focus'
  | 'timer'
  | 'stop'
  | 'subtask'
  | 'link'
  | 'blocked'
  | 'flag'
  | 'calendar'
  | 'bug'
  | 'paperclip'
  | 'reply'
  | 'archive'
  | 'trash'
  | 'pencil'
  | 'drag'
  | 'chevron-left'
  | 'music'
  | 'shield'
  | 'logout'
  | 'users'
  | 'building'
  | 'eye'
  | 'eye-off'
  | 'bold'
  | 'italic'
  | 'heading'
  | 'list'
  | 'checklist'
  | 'quote'
  | 'code'

const paths: Record<IconName, ReactNode> = {
  bold: (
    <>
      <path d="M7.5 5h4.75a3.25 3.25 0 0 1 0 6.5H7.5z" />
      <path d="M7.5 11.5h5.75a3.75 3.75 0 0 1 0 7.5H7.5z" />
    </>
  ),
  italic: <path d="M14.5 5h-4.5M14 19H9.5M14.5 5l-4 14" />,
  heading: <path d="M6 5v14M18 5v14M6 12h12" />,
  list: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <circle cx="4.5" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  checklist: (
    <>
      <path d="M11 6h9M11 12h9M11 18h9" />
      <path d="M3 6l1.5 1.5L7.5 4.5" />
      <path d="M3 12l1.5 1.5L7.5 10.5" />
      <path d="M3 18l1.5 1.5L7.5 16.5" />
    </>
  ),
  quote: (
    <>
      <path d="M4 5v14" />
      <path d="M9 6h11M9 12h11M9 18h6" />
    </>
  ),
  code: <path d="M9 8l-5 4 5 4M15 8l5 4-5 4" />,
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M9.9 5.2A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 3.9M6.2 6.2A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3.1-.5" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
    </>
  ),
  building: (
    <>
      <rect x="5" y="4" width="14" height="16" rx="1" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M10.5 20v-4h3v4" />
    </>
  ),
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5L21 21" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  check: <path d="M4 12l5 5L20 6" />,
  'chevron-down': <path d="M6 9l6 6 6-6" />,
  'chevron-right': <path d="M9 6l6 6-6 6" />,
  bell: (
    <>
      <path d="M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 2 6.5H4c.5-1 2-2.5 2-6.5z" />
      <path d="M10 18.5a2 2 0 0 0 4 0" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  play: <path d="M8 5l11 7-11 7z" />,
  pause: <path d="M8 5v14M16 5v14" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  at: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
    </>
  ),
  'my-tasks': (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8.5 12l2.5 2.5L16 9" />
    </>
  ),
  kanban: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16M15 4v16" />
    </>
  ),
  gantt: <path d="M4 5h9M4 5v14M8 10h9M12 15h7" />,
  report: <path d="M4 4v16h16M8 16v-5M13 16V8M18 16v-8" />,
  focus: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  timer: (
    <>
      <path d="M10 2h4" />
      <path d="M12 4v3" />
      <circle cx="12" cy="14" r="8" />
      <path d="M12 14V10" />
    </>
  ),
  stop: <rect x="6" y="6" width="12" height="12" rx="2" />,
  subtask: (
    <>
      <path d="M8 4v9a3 3 0 0 0 3 3h5" />
      <path d="M13 13l3 3-3 3" />
    </>
  ),
  link: (
    <>
      <path d="M10.5 13.5a4 4 0 0 0 5.66 0l2.5-2.5a4 4 0 0 0-5.66-5.66l-1.5 1.5" />
      <path d="M13.5 10.5a4 4 0 0 0-5.66 0l-2.5 2.5a4 4 0 0 0 5.66 5.66l1.5-1.5" />
    </>
  ),
  blocked: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  flag: (
    <>
      <path d="M6 21V4" />
      <path d="M6 4h11l-2 3 2 3H6" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M4 9h16M8 3v4M16 3v4" />
    </>
  ),
  bug: (
    <>
      <rect x="8" y="8" width="8" height="10" rx="4" />
      <path d="M8 12H4M8 15H4M16 12h4M16 15h4M9 8l-1.5-2.5M15 8l1.5-2.5M12 18v3" />
    </>
  ),
  paperclip: (
    <path d="M18 8.5l-7.6 7.6a3 3 0 0 1-4.2-4.2l7.6-7.6a2 2 0 0 1 2.8 2.8l-7.1 7.1a1 1 0 0 1-1.4-1.4l6.4-6.4" />
  ),
  reply: (
    <>
      <path d="M9 7L4 12l5 5" />
      <path d="M4 12h9a6 6 0 0 1 6 6v1" />
    </>
  ),
  archive: (
    <>
      <rect x="4" y="4" width="16" height="4" rx="1" />
      <path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
    </>
  ),
  pencil: (
    <>
      <path d="M4 20h4L18 10l-4-4L4 16z" />
      <path d="M13 7l4 4" />
    </>
  ),
  drag: (
    <>
      <circle cx="9" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  'chevron-left': <path d="M15 6l-6 6 6 6" />,
  music: (
    <>
      <path d="M9 18V5l10-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="16" cy="16" r="3" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </>
  ),
  logout: (
    <>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
      <path d="M15 8l4 4-4 4" />
      <path d="M9 12h10" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3.5 3.5 0 0 1 0 6.6" />
      <path d="M17.5 14.3A5.5 5.5 0 0 1 20.5 19" />
    </>
  ),
}

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
  size?: number
}

export function Icon({ name, size = 20, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {paths[name]}
    </svg>
  )
}
